import SocketClient, { Socket } from 'socket.io-client';
import EventBus from '@/config/event-handler';
import { SOCKET_URL } from '@/config/websocket';
import {
    IOrderNotificationMessage,
    IPaymentNotificationMessage,
    NotifyVendorEnum,
    PaymentRecordType,
} from '@/views/Payments';
import { cloneDeep, debounce, isEqual } from 'lodash';
import { IPaymentTransaction } from '@/views/Orders/types';
import { IQsrOrder } from '@/views/QsrOrders/types';
import { vendorFailedPaymentEvent, vendorOrderEvent, vendorPaymentEvent } from './event.const';

export interface ITableParam {
    id: string;
    f1?: string;
    f2?: string;
}

export interface ISocketFilter {
    selectedTable?: ITableParam[];
    paymentType?: PaymentRecordType;
}

interface ISocketIOConnectParam {
    accessToken: string;
    restaurantId: string;
    tokenType: string;
    filter?: ISocketFilter;
}

export enum VendorEventTypeEnum {
    PaymentRecord = 'paymentRecord',
    Order = 'order',
    FailedPayment = 'failedPayment',
}

export enum SocketMessageEnum {
    VendorJoin = 'vendorJoin',
    Connect = 'connect',
    ConnectError = 'connect_error',
}

export class SocketConnector {
    private socket: Socket | undefined;

    static instance: SocketConnector;

    static getInstance = () => {
        if (!SocketConnector.instance) {
            SocketConnector.instance = new SocketConnector();
        }
        return SocketConnector.instance;
    };

    private init = false;

    private connected = false;

    private msgMap: { [key: string]: IPaymentTransaction } = {};

    private orderMsgMap: { [key: string]: IQsrOrder } = {};

    private failedPaymentMsgMap: { [key: string]: IPaymentTransaction } = {};

    private lastConfig: any = null;

    private eventHandler = () => {
        EventBus.dispatch(vendorPaymentEvent, Object.values(this.msgMap));
        this.msgMap = {};
    };

    private orderEventHandler = () => {
        EventBus.dispatch(vendorOrderEvent, Object.values(this.orderMsgMap));
        this.orderMsgMap = {};
    };

    private failedPaymentEventHandler = () => {
        EventBus.dispatch(vendorFailedPaymentEvent, Object.values(this.failedPaymentMsgMap));
        this.failedPaymentMsgMap = {};
    };

    private eventDebounce = debounce(this.eventHandler, 256);

    private orderEventDebounce = debounce(this.orderEventHandler, 256);

    private failedPaymentEventDebounce = debounce(this.failedPaymentEventHandler, 256);

    public disconnect() {
        if (this.socket) {
            this.socket.disconnect();
            this.connected = false;
        }
    }

    public destroy() {
        if (this.socket) {
            this.socket.disconnect();
            this.socket = undefined;
            this.connected = false;
        }
    }

    public socketOpen(params: any): Promise<Socket> {
        const selectedTables = params.selectedTables || [];

        return new Promise<Socket>((resolve, reject) => {
            if (!this.socket) {
                const socket = SocketClient(SOCKET_URL || '', {
                    auth: params.auth,
                    transports: ['websocket'],
                });

                this.socket = socket;

                socket.on(SocketMessageEnum.Connect, () => {
                    console.log('connected');
                    console.log('socket connected with id ', socket.id);
                    socket.emit(SocketMessageEnum.VendorJoin, selectedTables);
                    resolve(socket);
                });

                socket.on(VendorEventTypeEnum.PaymentRecord, this.paymentMessageHandler);

                socket.on(VendorEventTypeEnum.Order, this.orderMessageHandler);

                socket.on(VendorEventTypeEnum.FailedPayment, this.failedPaymentMessageHandler);

                socket.on(SocketMessageEnum.ConnectError, (err: any) => {
                    console.error('Error in creating socket ', err);
                    reject(err);
                });
            } else if (!this.socket.connected) {
                this.socket.connect();

                this.socket.on(SocketMessageEnum.Connect, () => {
                    if (this.socket) {
                        console.log('reconnected with tables:', selectedTables);
                        this.socket.emit(SocketMessageEnum.VendorJoin, selectedTables);

                        resolve(this.socket);
                    } else {
                        reject();
                    }
                });

                this.socket.on(SocketMessageEnum.ConnectError, (err: any) => {
                    console.error('Error in creating socket ', err);
                    reject(err);
                });
            } else {
                resolve(this.socket);
            }
        });
    }

    public connect(params: ISocketIOConnectParam): Promise<Socket> {
        this.init = true;
        if (this.connected) {
            this.disconnect();
        }
        this.lastConfig = cloneDeep(params);
        return this.socketOpen({
            auth: {
                token: params.accessToken,
                restaurantId: params.restaurantId,
                tokenType: params.tokenType,
            },
            filter: params.filter,
        }).then((res) => {
            this.connected = true;
            return res;
        });
    }

    public modify(params: ISocketIOConnectParam, applySelectedTable?: boolean): Promise<any> {
        if (!this.init) {
            return Promise.resolve();
        }
        if (applySelectedTable) {
            params.filter = this.lastConfig?.filter;
        }
        if (isEqual(this.lastConfig, params)) {
            return Promise.resolve();
        }
        return this.connect(params);
    }

    private paymentMessageHandler = (msg: IPaymentNotificationMessage) => {
        if (this.msgMap.hasOwnProperty(msg.details.id)) {
            const m = this.msgMap[msg.details.id];
            const idx = m.paymentRecord?.findIndex((o) => o.id === msg.details?.paymentRecord?.[0].id) || -1;
            if (idx === -1) {
                this.msgMap[msg.details.id] = {
                    ...msg.details,
                    paymentRecord: [...(msg.details.paymentRecord || []), ...(m.paymentRecord || [])],
                };
            }
        } else {
            this.msgMap[msg.details.id] = { ...msg.details, eventName: msg.eventName };
        }
        this.eventDebounce();
    };

    private orderMessageHandler = (msg: IOrderNotificationMessage) => {
        this.orderMsgMap[msg.details.id] = { ...msg.details, silent: msg.eventName === NotifyVendorEnum.UpdateStatus };
        this.orderEventDebounce();
    };

    private failedPaymentMessageHandler = (msg: IPaymentNotificationMessage) => {
        this.failedPaymentMsgMap[msg.details.id] = { ...msg.details, eventName: msg.eventName };
        this.failedPaymentEventDebounce();
    };
}
